import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { THEMES, hasPermission, type SessionUser, type Theme } from '@voice/shared';
import { api } from '../lib/api';
import { ME_KEY } from '../lib/auth';
import { NAV } from '../lib/nav';
import { applyTheme } from '../lib/theme';
import styles from './Layout.module.css';
import { Wordmark } from './Wordmark';
import { SoftphoneDock } from './SoftphoneDock';
import { RealtimeProvider } from '../lib/realtime';
import { SoftphoneProvider } from '../lib/softphone';

const THEME_LABELS: Record<Theme, string> = { system: 'System', dark: 'Dunkel', light: 'Hell' };

export function Layout({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();

  const setTheme = useMutation({
    mutationFn: (theme: Theme) => api<SessionUser>('/me', { method: 'PATCH', body: { theme } }),
    onMutate: (theme) => applyTheme(theme),
    onSuccess: (updated) => queryClient.setQueryData(ME_KEY, updated),
  });

  const logout = useMutation({
    mutationFn: () => api('/auth/logout', { method: 'POST' }),
    onSuccess: () => queryClient.setQueryData(ME_KEY, null),
  });

  return (
    <RealtimeProvider>
    <SoftphoneProvider>
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <Wordmark />
        </div>

        <nav aria-label="Hauptnavigation" className={styles.nav}>
          {NAV.filter((item) => !item.permission || hasPermission(user.permissions, item.permission)).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end ?? false} className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
              <item.icon size={18} strokeWidth={1.5} aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.account}>
          <div className={styles.who}>
            <span className={styles.name}>{user.name}</span>
            <span className={styles.role}>{user.role.label}</span>
          </div>

          <div className={styles.themes} role="group" aria-label="Farbschema">
            {THEMES.map((theme) => (
              <button key={theme} type="button" className={styles.theme} aria-pressed={user.theme === theme} onClick={() => setTheme.mutate(theme)}>
                {THEME_LABELS[theme]}
              </button>
            ))}
          </div>

          <button type="button" className={styles.logout} onClick={() => logout.mutate()} disabled={logout.isPending}>
            <LogOut size={18} strokeWidth={1.5} aria-hidden="true" />
            Abmelden
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        <Outlet />
      </main>
      <SoftphoneDock />
    </div>
    </SoftphoneProvider>
    </RealtimeProvider>
  );
}
